// React
import { useMemo } from 'react';

// Libraries
import { format } from 'date-fns';

// shadcn/ui
import { Counter } from '@ui/counter';
import { CalendarDatePicker } from '@ui/calendar-date-picker';

// Shared components
import { DataTableShortcuts } from '@components/DataTable/DataTableShortcuts';
import { DataTableProvider, DataTableContent } from '@components/DataTable';
import {
  FilterChipsList,
  buildFilterOptions,
  toChipGroups,
  type FilterCategory,
} from '@components/filters';
import { CalendarIcon } from '@components/icons';

// Local components
import { EventsTabs } from '../../sections/EventsTabs';
import { EventsToolbar } from '../../sections/EventsToolbar';
import { EventsCardList } from '../../sections/EventsCardList';
import { getEventsColumns } from './columns';

// Hooks
import { useTranslation } from '@hooks/useTranslation';

// Types
import type { AllEventsPageProps, EventListItem } from '../../types';
import { EVENT_FORMAT_VALUES, EVENT_STATUS_FILTER_VALUES } from '../../types';
import { ORDER_STATUS_FILTER_VALUES } from '@components/Sales/OrderManagement/types';

import { FIELD_SURFACE_BORDERED, FILTER_SURFACE } from "@/components/ui/field-surface";
import { cn } from "@/lib/utils";
const getEventRowId = (event: EventListItem) => event.id;

const EMPTY_STRING_ARRAY: string[] = [];
const DEFAULT_EVENTS: EventListItem[] = [];

// Matches the desktop CalendarDatePicker trigger's date-display format
// (calendar-date-picker.tsx) so the mobile date trigger shows the same string.
const DATE_DISPLAY_FORMAT = 'dd MMM yyyy';

export function AllEventsPage({
  currentTab = 'all',
  totalCount = 0,
  events = DEFAULT_EVENTS,
  salespersons,
  selectedSalespersonIds = EMPTY_STRING_ARRAY,
  onTabChange,
  onSalespersonChange,
  selectedFormats = EMPTY_STRING_ARRAY,
  onFormatsChange,
  selectedOrderStatuses = EMPTY_STRING_ARRAY,
  onOrderStatusesChange,
  selectedEventStatuses = EMPTY_STRING_ARRAY,
  onEventStatusesChange,
  onNewOrder,
  selectedDate,
  onDateChange,
  onOpenDateFilter,
  isLoading = false,
  isFetching = false,
  onEventIdClick,
  rowSelection,
  onRowSelectionChange,
  onGenerateEvent,
  pagination,
  currentUser,
}: AllEventsPageProps) {
  const {t} = useTranslation();
  const title = t('title');
  const countText = isLoading ? '...' : totalCount.toLocaleString();

  const columns = useMemo(
    () => getEventsColumns({onEventIdClick, onGenerateEvent, currentUser}),
    [onEventIdClick, onGenerateEvent, currentUser],
  );

  const categories = useMemo<FilterCategory[]>(() => [
    {
      key: 'format',
      label: t('toolbar.filter_categories.format'),
      options: buildFilterOptions(t, 'toolbar.formats', EVENT_FORMAT_VALUES),
      selected: selectedFormats,
      onChange: onFormatsChange,
    },
    {
      key: 'salesperson',
      label: t('toolbar.filter_categories.salesperson'),
      options: salespersons.map((sp) => ({value: String(sp.id), label: sp.name})),
      selected: selectedSalespersonIds,
      onChange: onSalespersonChange,
      searchable: true,
    },
    {
      key: 'order_status',
      label: t('toolbar.filter_categories.order_status'),
      options: buildFilterOptions(t, 'toolbar.order_statuses', ORDER_STATUS_FILTER_VALUES),
      selected: selectedOrderStatuses,
      onChange: onOrderStatusesChange,
    },
    {
      key: 'event_status',
      label: t('toolbar.filter_categories.event_status'),
      options: buildFilterOptions(t, 'toolbar.event_statuses', EVENT_STATUS_FILTER_VALUES),
      selected: selectedEventStatuses,
      onChange: onEventStatusesChange,
    },
  ], [
    t,
    salespersons,
    selectedSalespersonIds,
    onSalespersonChange,
    selectedFormats,
    onFormatsChange,
    selectedOrderStatuses,
    onOrderStatusesChange,
    selectedEventStatuses,
    onEventStatusesChange,
  ]);

  const chipGroups = useMemo(() => toChipGroups(categories), [categories]);

  return (
    <DataTableShortcuts>
      <div className="flex flex-col gap-4 h-full min-h-0 px-6 sm:px-8 lg:px-16">
        <div className="hidden md:flex items-center gap-3 flex-shrink-0">
          <h1 className="font-kapra-expanded text-2xl font-black uppercase leading-[1.5] text-off-black">
            {title}
          </h1>
          <Counter value={countText} data-loading={isLoading} />
        </div>

        <div className="flex flex-wrap items-center justify-between gap-4 flex-shrink-0">
          <div className="flex flex-wrap items-center gap-4">
            <EventsTabs
              currentTab={currentTab}
              onTabChange={onTabChange}
            />
            <CalendarDatePicker
              value={selectedDate}
              onChange={onDateChange}
              placeholder={t('toolbar.select_date')}
              className={cn(FILTER_SURFACE, 'hidden md:flex md:w-117')}
              showClear
            />
          </div>
          <EventsToolbar
            categories={categories}
            onNewOrder={onNewOrder}
            className="hidden md:block"
          />
        </div>

        {/* Mobile date-filter trigger — styled as the Figma "Value input"
            (regular weight, grey-light placeholder), not a pill button. */}
        <button
          type="button"
          onClick={onOpenDateFilter}
          className={cn(FIELD_SURFACE_BORDERED, FILTER_SURFACE, "flex w-full touch-manipulation items-center justify-between px-6 py-4 md:hidden focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-off-black/30")}
        >
          <span
            className={`truncate font-inter text-nav font-normal leading-normal ${selectedDate ? 'text-off-black' : 'text-grey-light'}`}
          >
            {selectedDate ? format(selectedDate, DATE_DISPLAY_FORMAT) : t('toolbar.select_date')}
          </span>
          <CalendarIcon className="size-4 shrink-0 text-off-black" aria-hidden="true" />
        </button>

        <FilterChipsList groups={chipGroups} />

        <div className="relative flex-1 min-h-0 hidden md:flex flex-col">
          <DataTableProvider
            columns={columns}
            data={events}
            getRowId={getEventRowId}
            rowSelection={rowSelection}
            onRowSelectionChange={onRowSelectionChange}
          >
            <DataTableContent
              emptyTitle="No events found"
              className="[&_table]:min-w-[1340px]"
            />
          </DataTableProvider>
        </div>

        <EventsCardList
          events={events}
          isLoading={isLoading}
          isFetching={isFetching}
          onEventIdClick={onEventIdClick}
          pagination={pagination}
          className="pb-4 flex-1 min-h-0 md:hidden"
        />
      </div>
    </DataTableShortcuts>
  );
}
